/**
 * Pick-walking — Allen's affordance (2026-07-25): animators click ONE joint
 * and keyboard-walk the skeleton from there (the Maya idiom: up = parent,
 * down = first child, left/right = siblings). Selection is already store
 * state in Document vocabulary (ADR-006 §13.2), so walking is pure hierarchy
 * math over rig data — no picking machinery, no engine types.
 *
 * The walk speaks the SAME vocabulary the skeleton draws (bone-vocabulary):
 * hidden bones are bridged through, exactly like bone-visual's segments — the
 * measured topology fact that forces it is the INLINE neck (Head's parent IS
 * NeckTwist02 on Tripo rigs): without bridging, walking up from Head would
 * strand on a bone the surface never shows. Which twists are hidden is the
 * MAPPER's verdict, not the name's (#70): NeckTwist01 mapped, so the walk now
 * stops there on the way up from Head; NeckTwist02 did not, so it is still
 * bridged through.
 * Engine-free (session-no-engine). Depends on: bone-vocabulary.
 * Depended on by: main (the arrow keymap).
 */
import { isPosableBone } from './bone-vocabulary.js';

/** The rig facts the walker needs — RigDescriptor bones carry exactly this. */
export interface WalkableBone {
  readonly name: string;
  readonly parent: string | null;
}

export type WalkDirection = 'parent' | 'child' | 'nextSibling' | 'prevSibling';

export interface PickWalker {
  /**
   * The bone one step from `from`, or null when the step does not exist
   * (top of the hierarchy, a leaf, an unknown bone). Never returns a hidden
   * bone; never throws — a dead end is a fact, not a fault.
   */
  walk(from: string, direction: WalkDirection): string | null;
}

export function createPickWalker(bones: readonly WalkableBone[], mapped: ReadonlySet<string>): PickWalker {
  const parentOf = new Map<string, string | null>();
  for (const b of bones) parentOf.set(b.name, b.parent);
  const posable = (name: string): boolean => isPosableBone(name, mapped);

  /** Nearest POSABLE ancestor, bridging through hidden bones (inline twists). */
  const posableParent = (name: string): string | null => {
    let p = parentOf.get(name) ?? null;
    while (p !== null && !posable(p)) p = parentOf.get(p) ?? null;
    return p;
  };

  // children[P] = posable bones whose nearest posable ancestor is P, in rig
  // (file) order — the deterministic sibling order.
  const children = new Map<string, string[]>();
  for (const b of bones) {
    if (!posable(b.name)) continue;
    const p = posableParent(b.name);
    if (p === null) continue;
    const list = children.get(p) ?? [];
    list.push(b.name);
    children.set(p, list);
  }

  return {
    walk(from, direction) {
      if (!parentOf.has(from) || !posable(from)) return null;
      switch (direction) {
        case 'parent':
          return posableParent(from);
        case 'child':
          return children.get(from)?.[0] ?? null;
        case 'nextSibling':
        case 'prevSibling': {
          const p = posableParent(from);
          if (p === null) return null;
          const siblings = children.get(p) as string[];
          const at = siblings.indexOf(from);
          // Wrap: cycling beats stranding at a hand's first/last digit.
          const step = direction === 'nextSibling' ? 1 : -1;
          const next = siblings[(at + step + siblings.length) % siblings.length] as string;
          return next === from ? null : next; // an only child has no siblings
        }
      }
    },
  };
}
